import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Trophy, Calendar, MapPin, Users, Clock, ArrowRight, 
  TrendingUp, Award, Zap, Target, Star
} from 'lucide-react';

const MatchSummaryCard = ({ session, stats, statLabels, onViewFeedback, onBackToHome }) => {
  if (!session) return null;

  const formatPosition = (pos) => {
    return pos?.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ') || 'Unknown';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Calculate total events/actions
  const totalActions = Object.values(stats || {}).reduce((sum, val) => sum + (val || 0), 0);
  
  // Find top stats (highest values)
  const sortedStats = Object.entries(stats || {})
    .filter(([_, val]) => val > 0)
    .sort((a, b) => b[1] - a[1]);
  
  const topStats = sortedStats.slice(0, 3);
  const otherStats = sortedStats.slice(3);

  // Sport icons
  const getSportIcon = (sport) => {
    const icons = {
      'football': '🏈',
      'soccer': '⚽',
      'basketball': '🏀',
      'baseball': '⚾',
      'softball': '🥎',
      'volleyball': '🏐',
      'lacrosse': '🥍',
      'hockey': '🏒',
      'tennis': '🎾',
      'swimming': '🏊',
      'track_field': '🏃',
      'wrestling': '🤼',
      'golf': '⛳',
      'gymnastics': '🤸',
      'cross_country': '🏃',
    };
    return icons[sport] || '🏆';
  };

  const formatStatKey = (key) => {
    return statLabels?.[key] || key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4">
      <div className="max-w-lg mx-auto space-y-4">
        {/* Header Card - Game Complete */}
        <Card className="bg-gradient-to-br from-green-600 to-emerald-700 border-0 overflow-hidden">
          <CardContent className="pt-6 pb-6 text-center relative">
            <div className="absolute top-2 right-2">
              <Badge className="bg-white/20 text-white border-0">
                {session.points_earned || 0} XP
              </Badge>
            </div>
            
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-white/20 flex items-center justify-center">
              <span className="text-4xl">{getSportIcon(session.sport)}</span>
            </div>
            
            <h1 className="text-2xl font-bold text-white mb-1">Game Complete!</h1>
            <p className="text-green-100">Great work tracking your stats</p>
          </CardContent>
        </Card>

        {/* Match Info Card */}
        <Card className="bg-slate-800/90 border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-400" />
              Match Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
              <div className="flex items-center gap-2 text-slate-400">
                <Users className="w-4 h-4" />
                <span>Opponent</span>
              </div>
              <span className="text-white font-medium">{session.opponent_name}</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
              <div className="flex items-center gap-2 text-slate-400">
                <Calendar className="w-4 h-4" />
                <span>Date</span>
              </div>
              <span className="text-white font-medium">{formatDate(session.game_date)}</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
              <div className="flex items-center gap-2 text-slate-400">
                <Target className="w-4 h-4" />
                <span>Position</span>
              </div>
              <span className="text-white font-medium">{formatPosition(session.position)}</span>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
              <div className="flex items-center gap-2 text-slate-400">
                <MapPin className="w-4 h-4" />
                <span>Location</span>
              </div>
              <Badge className={session.is_home_game ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-blue-500/20 text-blue-400 border-blue-500/30'}>
                {session.is_home_game ? 'Home' : 'Away'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Stats Summary Card */}
        <Card className="bg-slate-800/90 border-slate-700">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-400" />
                Stats Recap
              </CardTitle>
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                {totalActions} Total
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {totalActions === 0 ? (
              <p className="text-slate-400 text-center py-4">No stats recorded in this session</p>
            ) : (
              <div className="space-y-4">
                {/* Top Stats - Highlighted */}
                {topStats.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-500 uppercase tracking-wide flex items-center gap-1">
                      <Star className="w-3 h-3" /> Top Performance
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {topStats.map(([key, value], index) => (
                        <div 
                          key={key}
                          className={`p-3 rounded-lg text-center ${
                            index === 0 
                              ? 'bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30' 
                              : 'bg-slate-700/50'
                          }`}
                        >
                          <div className={`text-2xl font-bold ${index === 0 ? 'text-amber-400' : 'text-white'}`}>
                            {value}
                          </div>
                          <div className="text-xs text-slate-400 mt-1 leading-tight break-words" title={formatStatKey(key)}>
                            {formatStatKey(key)}
                          </div>
                          {index === 0 && (
                            <Award className="w-4 h-4 text-amber-400 mx-auto mt-1" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Other Stats */}
                {otherStats.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Other Stats</p>
                    <div className="grid grid-cols-4 gap-2">
                      {otherStats.map(([key, value]) => (
                        <div key={key} className="p-2 bg-slate-700/30 rounded-lg text-center">
                          <div className="text-lg font-semibold text-white">{value}</div>
                          <div className="text-xs text-slate-500 leading-tight break-words" title={formatStatKey(key)}>{formatStatKey(key)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Session Duration */}
        {session.duration_minutes && (
          <div className="flex items-center justify-center gap-2 text-slate-400">
            <Clock className="w-4 h-4" />
            <span className="text-sm">Session Duration: {session.duration_minutes} minutes</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3 pt-2">
          <Button
            className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white py-6 font-semibold"
            onClick={onViewFeedback}
            data-testid="view-ai-feedback-btn"
          >
            <TrendingUp className="w-5 h-5 mr-2" />
            View AI Performance Analysis
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          
          <Button
            variant="ghost"
            className="w-full text-slate-400 hover:text-white hover:bg-slate-800"
            onClick={onBackToHome}
            data-testid="back-to-sessions-btn"
          >
            Back to Sessions
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MatchSummaryCard;
